import React from "react"
import { Link } from "react-router-dom"
import styled from "styled-components"
import { Theme } from "@pga/pga-component-library"
import { Button } from "../Button"
import { formatDate } from "../utils/formatter"
import { mapPtmApplicationStatus } from "../../utils/prospect/ptmApplicationStatus"
import {
  blueGrey,
  darkgrey,
  lightBlue,
  primary,
  textGrey,
  white,
} from "../../assets/colors"
import {
  BACKGROUND_CHECK_STEP_STATE,
  PDR_STEP_STATE,
  resolveBackgroundCheckStep,
  resolvePdrStep,
} from "./reinstatementStepStatus"

export const REINSTATEMENT_STEPS_TITLE = "Steps to Reinstatement"
export const BACKGROUND_CHECK_PAGE_PATH = "/background-check"
export const BACKGROUND_CHECK_TIMING_NOTE =
  "A background check is only valid for one year. Submit yours when you are close to completing your PDR requirement so it is still current when you apply."

export const PDR_STEP_MET_COPY =
  "You have met the PDR requirement for reinstatement."
export const PDR_STEP_NOT_MET_COPY =
  "Earn the remaining PDRs below to meet the PDR requirement for reinstatement."
export const PDR_STEP_CONTACT_COPY =
  "Contact Membership Services to get the latest PDR requirement in order to Reinstate."

export const PDR_STEP_LOADING_COPY = "Loading your PDR requirement…"

export const pdrBucketCopy = ({ label, earned, required, remaining }) =>
  remaining > 0
    ? `${label}: ${earned} of ${required} earned, ${remaining} remaining`
    : `${label}: ${earned} of ${required} earned`

const STEP_STATUS_LABEL = {
  required: "Required",
  complete: "Complete",
  inProgress: "In Progress",
  incomplete: "Not Started",
  expired: "Expired",
  notApproved: "Not Approved",
  locked: "Locked",
  ready: "Ready",
}

const Section = styled.section`
  width: 100%;
  margin-top: 8px;
  text-align: left;
  font-family: ${Theme.fonts.Montserrat};
`

const Heading = styled.h2`
  margin: 0 0 4px;
  font-family: ${Theme.fonts.PlayfairDisplay};
  font-size: 20px;
  font-weight: 600;
  color: ${primary};
`

const Intro = styled.p`
  margin: 0 0 16px;
  font-size: 14px;
  color: ${textGrey};
`

const StepList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
`

const StepItem = styled.li`
  display: flex;
  gap: 12px;
  padding: 16px 0;
  border-top: 1px solid ${darkgrey};
`

const StepNumber = styled.span`
  flex: 0 0 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  color: ${({ $complete }) => ($complete ? white : primary)};
  background-color: ${({ $complete }) => ($complete ? lightBlue : white)};
  border: 2px solid ${({ $locked }) => ($locked ? darkgrey : lightBlue)};
`

const StepBody = styled.div`
  flex: 1;
  min-width: 0;
`

const StepHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const StepTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ $locked }) => ($locked ? blueGrey : primary)};
`

const StepStatus = styled.span`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: ${({ $complete }) => ($complete ? lightBlue : textGrey)};
`

const StepText = styled.p`
  margin: 6px 0 0;
  font-size: 14px;
  color: ${textGrey};
`

const Note = styled.p`
  margin: 8px 0 0;
  padding: 8px 12px;
  font-size: 13px;
  color: ${primary};
  background-color: #eef6fc;
  border-left: 3px solid ${lightBlue};
`

const BucketList = styled.ul`
  margin: 8px 0 0;
  padding-left: 20px;
  font-size: 14px;
  color: ${primary};
`

const StepLink = styled.a`
  display: inline-block;
  margin-top: 8px;
  font-size: 14px;
`

const StepRouterLink = styled(Link)`
  display: inline-block;
  margin-top: 8px;
  font-size: 14px;
  font-weight: 600;
`

const formatLongDate = date => (date ? formatDate(date, "MMMM D, YYYY") : null)

const Step = ({ number, title, status, complete, locked, children }) => (
  <StepItem>
    <StepNumber $complete={complete} $locked={locked} aria-hidden="true">
      {number}
    </StepNumber>
    <StepBody>
      <StepHeader>
        <StepTitle $locked={locked}>
          <span className="sr-only">Step {number}: </span>
          {title}
        </StepTitle>
        <StepStatus $complete={complete}>{status}</StepStatus>
      </StepHeader>
      {children}
    </StepBody>
  </StepItem>
)

const PDR_STEP_COPY = {
  [PDR_STEP_STATE.MET]: PDR_STEP_MET_COPY,
  [PDR_STEP_STATE.NOT_MET]: PDR_STEP_NOT_MET_COPY,
  [PDR_STEP_STATE.UNKNOWN]: PDR_STEP_CONTACT_COPY,
}

// Display only: Submit is gated by the background check, and Membership
// Services confirms PDRs when they review the application.
const PdrStep = ({
  pdrRequirements,
  loading,
  pdrResourcesUrl,
  pdrResourcesCopy,
}) => {
  const pdr = resolvePdrStep(loading ? null : pdrRequirements)

  return (
    <Step
      number={1}
      title="Satisfy PDR Requirement"
      status={
        pdr.complete ? STEP_STATUS_LABEL.complete : STEP_STATUS_LABEL.required
      }
      complete={pdr.complete}
    >
      <StepText aria-busy={loading ? "true" : undefined}>
        {loading ? PDR_STEP_LOADING_COPY : PDR_STEP_COPY[pdr.state]}
      </StepText>
      {pdr.requirements.length > 0 && (
        <BucketList aria-label="Reinstatement PDR requirement">
          {pdr.requirements.map((bucket, index) => (
            <li key={`${index}-${bucket.label}`}>{pdrBucketCopy(bucket)}</li>
          ))}
        </BucketList>
      )}
      {pdrResourcesUrl && (
        <StepLink
          href={pdrResourcesUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {pdrResourcesCopy}
        </StepLink>
      )}
    </Step>
  )
}

const backgroundCheckCopy = (backgroundCheck, submitted) => {
  switch (backgroundCheck.state) {
    case BACKGROUND_CHECK_STEP_STATE.APPROVED: {
      const expiresOn = formatLongDate(backgroundCheck.expiresAt)
      if (!expiresOn) return "Your background check is approved."
      const validUntil = `Your background check is approved and valid until ${expiresOn}.`
      return submitted
        ? validUntil
        : `${validUntil} Submit your application before it expires.`
    }
    case BACKGROUND_CHECK_STEP_STATE.IN_PROGRESS:
      return "Your background check is in progress."
    case BACKGROUND_CHECK_STEP_STATE.DENIED:
      return "Your background check was not approved. Contact PGA Membership Services for next steps."
    case BACKGROUND_CHECK_STEP_STATE.EXPIRED: {
      const expiredOn = formatLongDate(backgroundCheck.expiredAt)
      return expiredOn
        ? `Your last background check expired on ${expiredOn}. Reinstatement requires a new background check.`
        : "Your last background check has expired. Reinstatement requires a new background check."
    }
    default:
      return "Reinstatement requires a new background check."
  }
}

const backgroundCheckLinkLabel = backgroundCheck => {
  switch (backgroundCheck.state) {
    case BACKGROUND_CHECK_STEP_STATE.IN_PROGRESS:
      return backgroundCheck.canContinue ? "Continue Background Check" : null
    case BACKGROUND_CHECK_STEP_STATE.APPROVED:
    case BACKGROUND_CHECK_STEP_STATE.DENIED:
      return null
    default:
      return "Start Background Check"
  }
}

const backgroundCheckStatusLabel = state => {
  switch (state) {
    case BACKGROUND_CHECK_STEP_STATE.APPROVED:
      return STEP_STATUS_LABEL.complete
    case BACKGROUND_CHECK_STEP_STATE.IN_PROGRESS:
      return STEP_STATUS_LABEL.inProgress
    case BACKGROUND_CHECK_STEP_STATE.EXPIRED:
      return STEP_STATUS_LABEL.expired
    case BACKGROUND_CHECK_STEP_STATE.DENIED:
      return STEP_STATUS_LABEL.notApproved
    default:
      return STEP_STATUS_LABEL.incomplete
  }
}

const BackgroundCheckStep = ({ backgroundCheck, submitted }) => {
  const status = backgroundCheckStatusLabel(backgroundCheck.state)
  const linkLabel = backgroundCheckLinkLabel(backgroundCheck)

  return (
    <Step
      number={2}
      title="New Background Check"
      status={status}
      complete={backgroundCheck.complete}
    >
      <StepText>{backgroundCheckCopy(backgroundCheck, submitted)}</StepText>
      {!backgroundCheck.complete && !submitted && (
        <Note>{BACKGROUND_CHECK_TIMING_NOTE}</Note>
      )}
      {linkLabel && (
        <StepRouterLink to={BACKGROUND_CHECK_PAGE_PATH}>
          {linkLabel}
        </StepRouterLink>
      )}
    </Step>
  )
}

const SubmitStep = ({ unlocked, applyLabel, onApply }) => (
  <Step
    number={3}
    title="Submit Application"
    status={unlocked ? STEP_STATUS_LABEL.ready : STEP_STATUS_LABEL.locked}
    complete={false}
    locked={!unlocked}
  >
    <StepText id="reinstatement-submit-help">
      {unlocked
        ? "You are ready to submit your reinstatement application."
        : "Available once your background check is approved."}
    </StepText>
    <Button
      color="primary"
      className="mt-3 ml-0"
      disabled={!unlocked}
      aria-describedby="reinstatement-submit-help"
      onClick={unlocked ? onApply : undefined}
    >
      {applyLabel}
    </Button>
  </Step>
)

// The applicant cannot edit a submitted application, so changes go through
// Membership Services rather than the View Application page.
export const CHANGES_REQUESTED_COPY =
  "Membership Services requested changes to your reinstatement application. Contact them at the number above."
export const CHANGES_REQUESTED_LABEL = "Changes Requested"

const SUBMITTED_APPLICATION_COPY = {
  submitted:
    "Your reinstatement application was submitted. Membership Services will review it.",
  under_review:
    "Membership Services is reviewing your reinstatement application.",
  changes_requested: CHANGES_REQUESTED_COPY,
  approved: "Your reinstatement application was approved.",
}

const SUBMITTED_STATUS_LABEL = {
  changes_requested: CHANGES_REQUESTED_LABEL,
}

export const SUBMITTED_APPLICATION_INTRO =
  "You have submitted your reinstatement application."
export const VIEW_APPLICATION_LABEL = "View Application"

const SubmittedStep = ({ application, onViewApplication }) => {
  const status = String(application.status).toLowerCase()
  const statusLabel =
    SUBMITTED_STATUS_LABEL[status] ||
    mapPtmApplicationStatus(application.status)?.text

  return (
    <Step number={3} title="Submit Application" status={statusLabel} complete>
      <StepText>{SUBMITTED_APPLICATION_COPY[status]}</StepText>
      <Button
        color="primary"
        outline
        className="mt-3 ml-0"
        onClick={onViewApplication}
      >
        {VIEW_APPLICATION_LABEL}
      </Button>
    </Step>
  )
}

const ReinstatementSteps = ({
  loading,
  backgroundCheck,
  backgroundCheckOrder,
  applyLabel,
  onApply,
  pdrResourcesUrl,
  pdrResourcesCopy,
  pdrRequirements,
  pdrRequirementsLoading,
  application,
}) => {
  if (loading) {
    return (
      <Section aria-busy="true">
        <Heading>{REINSTATEMENT_STEPS_TITLE}</Heading>
        <Intro>Loading your reinstatement steps…</Intro>
      </Section>
    )
  }

  const bgc = resolveBackgroundCheckStep({
    backgroundCheck,
    order: backgroundCheckOrder,
  })
  const unlocked = bgc.complete

  return (
    <Section aria-labelledby="reinstatement-steps-heading">
      <Heading id="reinstatement-steps-heading">
        {REINSTATEMENT_STEPS_TITLE}
      </Heading>
      <Intro>
        {application
          ? SUBMITTED_APPLICATION_INTRO
          : "Complete these steps to apply for reinstatement."}
      </Intro>
      <StepList>
        <PdrStep
          pdrRequirements={pdrRequirements}
          loading={pdrRequirementsLoading}
          pdrResourcesUrl={pdrResourcesUrl}
          pdrResourcesCopy={pdrResourcesCopy}
        />
        <BackgroundCheckStep
          backgroundCheck={bgc}
          submitted={Boolean(application)}
        />
        {application ? (
          <SubmittedStep
            application={application}
            onViewApplication={onApply}
          />
        ) : (
          <SubmitStep
            unlocked={unlocked}
            applyLabel={applyLabel}
            onApply={onApply}
          />
        )}
      </StepList>
    </Section>
  )
}

export default ReinstatementSteps
